import React, { useEffect } from 'react';
import { Navigate, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '@fikasio/react-ui-components';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import SettingsAccount from 'components/settings/SettingsAccount';
import SettingsAdvanced from 'components/settings/SettingsAdvanced';
import SettingsBilling from 'components/settings/SettingsBilling';
import SettingsConnectors from 'components/settings/SettingsConnectors';
import SettingsGeneral from 'components/settings/SettingsGeneral';
import BasePage from 'components/UI/BasePage';
import links from 'utils/links';
import { spaLinkProps } from 'utils/spaLink';
import styles from './SettingsPage.module.css';

const SETTINGS_SECTIONS = [
  { id: 'general', labelKey: 'general', Component: SettingsGeneral },
  { id: 'account', labelKey: 'account', Component: SettingsAccount },
  { id: 'billing', labelKey: 'billing', Component: SettingsBilling },
  { id: 'connectors', labelKey: 'connectors', Component: SettingsConnectors },
  { id: 'advanced', labelKey: 'advanced', Component: SettingsAdvanced },
] as const;

type SettingsSectionId = typeof SETTINGS_SECTIONS[number]['id'];

const isSettingsSectionId = (value: string | undefined): value is SettingsSectionId => (
  SETTINGS_SECTIONS.some(section => section.id === value)
);

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { section: sectionParam } = useParams<{ section?: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });
  }, [location.pathname]);

  if (!sectionParam) {
    return <Navigate replace to={links.settings('general')} />;
  }

  if (!isSettingsSectionId(sectionParam)) {
    return <Navigate replace to={links.settings('general')} />;
  }

  const section = SETTINGS_SECTIONS.find(({ id }) => id === sectionParam)
    ?? SETTINGS_SECTIONS[0];
  const SectionComponent = section.Component;

  return (
    <>
      <Helmet>
        <title>{`${t(section.labelKey)} · ${t('settings')}`}</title>
      </Helmet>
      <BasePage>
        <Breadcrumb
          items={[
            { ...spaLinkProps(links.paths.home, navigate), label: t('home') },
            { label: t('settings') },
          ]}
        />
        <h4>{t('settings')}</h4>
        <div className={styles.layout}>
          <nav aria-label={t('settings')} className={styles.nav}>
            <ul className={styles.navList}>
              {
                SETTINGS_SECTIONS.map(({ id, labelKey }) => (
                  <li key={id}>
                    <NavLink
                      className={({ isActive }) => (
                        [styles.navLink, isActive ? styles.navLinkActive : '']
                          .filter(Boolean)
                          .join(' ')
                      )}
                      end
                      to={links.settings(id)}
                    >
                      {t(labelKey)}
                    </NavLink>
                  </li>
                ))
              }
            </ul>
          </nav>
          <div className={styles.content}>
            {
              section.id !== 'connectors' && (
                <h5 className={styles.sectionTitle}>{t(section.labelKey)}</h5>
              )
            }
            <SectionComponent />
          </div>
        </div>
      </BasePage>
    </>
  );
};

export default SettingsPage;
